import {useCommonItems} from "../../../../contexts/CommonItensProvider";
import {useAppContext} from "../../../../contexts/AppContext";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Grid,
    LinearProgress,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import {getSaldoArmarioData} from "./configs/functions";
import {carregaLockers} from "../equipments/configs/functions";
import {listaEmpresas} from "../../management/companies/configs/functions";
import {checkPermissionsAndRedirect, getFormErrorMessage, verifyFieldsAreFilled} from "../../../../configs/functions";
import {exportBalanceLockerToPDF, exportBalanceMachineToPDF} from "./configs/utils";
import LinkNavegacao from "../../../common/Link";
import HomeIcon from "@mui/icons-material/Home";
import PageTitle from "../../../common/PageTitle";
import {LockerIcon} from "../../../common/Icons";
import {SelectEmpresaFilter} from "../../../common/SelectEmpresaFilter";
import {FilterAlt} from "@mui/icons-material";
import {DataGrid, ptBR} from "@mui/x-data-grid";
import {CustomToolbarReports} from "../../../common/CustomToolbarReports";


export function SaldoArmario() {
    const { exibirAlerta } = useCommonItems();
    const { dadosUsuario } = useAppContext();

    const [numTotalItems, setNumTotalItems] = useState(0);

    const [balanceArmario, setDataBalanceArmario] = useState([]);
    const [armarios, setArmarios] = useState([]);
    const [empresas, setEmpresas] = useState([]);

    const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [isSearched, setIsSearched] = useState(false);
    const [selectedArmario, setSelectedArmario] = useState(null);

    const {
        handleSubmit,
        setValue,
        getValues,
        clearErrors,
        reset,
        control,
        formState: { errors },
    } = useForm();

    // uma linha por porta, ordenadas pelo número da porta
    const rows = useMemo(() => {
        const base = Array.isArray(balanceArmario) ? balanceArmario : [];
        return [...base]
            .sort((a, b) => {
                const pa = parseInt(String(a.numero_porta).split('-')[0]) || 0;
                const pb = parseInt(String(b.numero_porta).split('-')[0]) || 0;
                return pa - pb;
            })
            .map((item, idx) => ({ ...item, id: `porta-${item.numero_porta}-${idx}` }));
    }, [balanceArmario]);

    const columns = useMemo(() => [
        {
            field: 'numero_porta',
            headerName: 'Porta',
            sortable: false,
            minWidth: 120,
            flex: 0.5,
            renderCell: (params) => (
                <Typography variant="body2" color={!params.value && 'textSecondary'}>
                    {String(params.value ?? '') || 'Vazio'}
                </Typography>
            ),
        },
        {
            field: 'produtoCa',
            headerName: 'CA',
            sortable: false,
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" color={!params.value && 'textSecondary'}>{params.value || 'Vazio'}</Typography>
            ),
        },
        {
            field: 'produtoNome',
            headerName: 'Produto',
            sortable: false,
            minWidth: 300,
            flex: 2,
            renderCell: (params) => (
                <Typography variant="body2" color={!params.value && 'textSecondary'}>{params.value || 'Vazio'}</Typography>
            ),
        },
        {
            field: 'produtoCodExterno',
            headerName: 'Cód. Produto',
            sortable: false,
            minWidth: 120,
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" color={!params.value && 'textSecondary'}>{params.value || 'Vazio'}</Typography>
            ),
        },
        {
            field: 'categoria',
            headerName: 'Categoria',
            sortable: false,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" color={!params.value && 'textSecondary'}>{params.value || 'Vazio'}</Typography>
            ),
        },
        {
            field: 'unidade_medida',
            headerName: 'Unidade Medida',
            sortable: false,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" color={!params.value && 'textSecondary'}>{params.value || 'Vazio'}</Typography>
            ),
        },
        {
            field: 'quantidade',
            headerName: 'Quantidade',
            sortable: false,
            minWidth: 150,
            flex: 0.5,
            renderCell: (params) => (
                <Typography variant="body2" color={!params.value && 'textSecondary'}>{params.value ?? '0'}</Typography>
            ),
        },
        {
            field: 'status_porta',
            headerName: 'Status Porta',
            sortable: false,
            minWidth: 130,
            flex: 0.5,
            renderCell: (params) => (
                <Typography variant="body2" color={!params.value && 'textSecondary'}>
                    {params.value.charAt(0).toUpperCase()}{params.value.slice(1)}
                </Typography>
            ),
        },
    ], []);

    async function loadReportData(id_armario) {
        if (!id_armario) return;

        try {
            setIsLoading(true);

            const filter = { id_armario };
            const res = await getSaldoArmarioData(filter);

            setDataBalanceArmario(Array.isArray(res.data.resultados) ? res.data.resultados : []);
            setNumTotalItems(res.data.numero_total);
        } catch (error) {
            exibirAlerta('Ops', 'Ocorreu um erro ao carregar dados do relatório.', 'error');
        } finally {
            setIsLoading(false);
        }
    }

    const onSubmit = async (data) => {
        try {
            setIsSearched(true);
            loadReportData(data.id_armario);
        } catch (error) {
            exibirAlerta('Erro', 'Erro ao carregar dados do relatório', 'error');
            setIsSearched(false);
        }
    }

    const getArmarios = useCallback(async (empresaId) => {
        try {
            const filterAll = {
                id_empresas: Array.isArray(empresaId) ? empresaId : [empresaId],
                isFilter: true
            };
            const response = await carregaLockers(filterAll);

            setArmarios(response.data.data);
        } catch (error) {
            exibirAlerta('Erro ao carregar os Armários', '', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [exibirAlerta]);

    const getEmpresasData = useCallback(async () => {
        try {
            const response = await listaEmpresas(null, null, null, true);
            const empresasData = response.data.data;
            setEmpresas(empresasData);

            if (empresasData.length > 0) {
                setSelectedEmpresaId(empresasData[0].id);
            }
        } catch (error) {
            exibirAlerta('Erro ao carregar as Empresas', '', 'error');
        }
    }, [exibirAlerta]);

    useEffect(() => {
        const requiredPermissionsView = ["admin", "admin_relatorio"];
        checkPermissionsAndRedirect(requiredPermissionsView);

        getEmpresasData();

        return () => {
            reset();
        }
    }, [getEmpresasData, reset]);

    useEffect(() => {
        if (selectedEmpresaId) {
            getArmarios(selectedEmpresaId);
        }
    }, [selectedEmpresaId]);

    function exportDataToPDF(visibleColumns, reportName) {
        try {
            if (!getValues('id_armario')) {
                return exibirAlerta('Ops', 'Selecione um armário', 'warning');
            }

            const selectedMachine = armarios.find(item => item.id === getValues('id_armario'));
            const empresaId = selectedMachine?.empresa?.id || null;
            const selectedEmpresa = empresas.find(item => item.id === empresaId);

            exportBalanceLockerToPDF({
                tableData: rows,
                visibleColumns,
                reportName,
                selectedMaquina: selectedMachine,
                selectedEmpresa: selectedEmpresa,
                dadosUsuario
            });
        } catch (error) {
            exibirAlerta('Erro', 'Erro ao exportar dados para PDF', 'error');
        }
    }

    return (
        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={3}
        >
            <Grid item xs={12}>
                <Breadcrumbs>
                    <LinkNavegacao to='/'><HomeIcon fontSize='small' /></LinkNavegacao>
                    <LinkNavegacao to='/epis'>Gestão de EPI's</LinkNavegacao>
                    <LinkNavegacao to='/epis/relatorios'>Relatórios</LinkNavegacao>
                    <Typography variant='span'>Saldo de Armários</Typography>
                </Breadcrumbs>
                <PageTitle icon={<LockerIcon fontSize='large' />} title="Saldo de Armários" />
            </Grid>

            <Grid container item xs={12} spacing={3} component="form" onSubmit={handleSubmit(onSubmit)}>
                <Grid item xs={12} md={6}>
                    <SelectEmpresaFilter
                        empresas={empresas}
                        id_empresas={empresas}
                        defaultValue={selectedEmpresaId}
                        onChangeValue={(value) => {
                            setIsSearched(false);
                            setSelectedEmpresaId(value);
                            setValue('id_armario', '', { shouldDirty: true });
                            setSelectedArmario(null);
                            setDataBalanceArmario([]);
                        }}
                        textHelper={false}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Controller
                        name='id_armario'
                        control={control}
                        rules={{ required: false }}
                        defaultValue={''}
                        render={({ field: { ref, onChange, ...field } }) => (
                            <TextField
                                {...field}
                                select
                                fullWidth
                                label="Armário"
                                onChange={(e) => {
                                    setIsSearched(false);
                                    setSelectedArmario(armarios.find(item => item.id === e.target.value));
                                    setValue('id_armario', e.target.value, { shouldDirty: true });
                                    if (e.target.value) {
                                        clearErrors("id_armario");
                                    }
                                }}
                                error={!!errors.id_armario}
                                helperText={getFormErrorMessage(errors, 'id_armario')}
                                size='small'
                                InputProps={{ style: { backgroundColor: '#fff' } }}
                            >
                                {armarios.length > 0 ? (
                                    [
                                        ...armarios.filter(option => option.status !== 0).map(option => (
                                            <MenuItem key={option.id} value={option.id}>
                                                {option.nome} - {option.tagIdentificacao} ({option.numeroPortas} portas)
                                            </MenuItem>
                                        )),
                                        ...armarios.filter(option => option.status === 0).map(option => (
                                            <MenuItem key={option.id} value={option.id} disabled>
                                                {option.nome} - {option.tagIdentificacao} ({option.numeroPortas} portas) - (Inativo)
                                            </MenuItem>
                                        ))
                                    ]
                                ) : (
                                    <MenuItem disabled>Nenhum disponível</MenuItem>
                                )}
                            </TextField>
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={1}>
                    <Button
                        type='submit'
                        color='primary'
                        variant='contained'
                        disabled={isLoading || Object.keys(errors).length > 0 || !verifyFieldsAreFilled([getValues('id_armario')])}
                        startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "textSecondary" }} /> : <FilterAlt />}
                    >
                        Filtrar
                    </Button>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <Box sx={{
                    height: balanceArmario.length > 0 ? "auto" : 400,
                    minHeight: 400,
                    width: '100%',
                    '& .MuiDataGrid-columnHeaders, .MuiDataGrid-toolbarContainer': {
                        backgroundColor: '#d8d8d8',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        borderRadius: 0
                    },
                }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        pageSizeOptions={[10, 20, 50, 100]}
                        rowCount={numTotalItems}
                        paginationMode="client"
                        sx={{
                            backgroundColor: '#fff',
                            '.MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 'bold !important',
                                overflow: 'visible !important',
                            }
                        }}
                        disableRowSelectionOnClick
                        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                        slots={{
                            toolbar: (props) =>
                                <CustomToolbarReports
                                    reportName="relatorio_saldo_armario"
                                    pdfExport={exportDataToPDF}
                                    props={props}
                                />,
                            loadingOverlay: LinearProgress,
                            noRowsOverlay: () => (
                                <Box style={{ display: "flex", width: '100%', textAlign: 'center', height: "100%", alignItems: 'center', justifyContent: 'center' }}>
                                    {isSearched ?
                                        <Typography variant='h6' color='textSecondary'>Nenhum registro encontrado para o filtro selecionado</Typography>
                                        :
                                        <Typography variant='h6' color='textSecondary'>Selecione o armário que deseja filtrar</Typography>
                                    }
                                </Box>
                            )
                        }}
                    />
                </Box>
            </Grid>
        </Grid>
    );
}